import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  PencilIcon,
  UserIcon,
  XMarkIcon,
  TagIcon,
  PhotoIcon,
  LinkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon,
  PauseIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";

import {
  getFirestore,
  collection,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";


export default function EditPromocionalProfile({ isOpen, onClose, userId }) {
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const db = getFirestore();

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const ref = collection(db, "users", userId, "promoProfiles");
        const snapshot = await getDocs(ref);
        const fetched = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((p) => p.status !== "deleted");
        setProfiles(fetched);
        setCurrentIndex(0);
      } catch (err) {
        console.error(err);
        setError("Error al cargar los perfiles.");
      }
    };

    if (isOpen) {
      fetchProfiles();
      setSuccess(false);
    }
  }, [isOpen, userId, db]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updated = [...profiles];
    updated[currentIndex][name] = value;
    setProfiles(updated);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const updated = [...profiles];
      updated[currentIndex].newImage = file;
      setProfiles(updated);
    }
  };

  const uploadProfileImage = async (file) => {
  if (!file) return null;

  try {
    // Crear ruta única en Firebase Storage para evitar sobreescritura
    const filePath = `promoProfiles/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    // Subir archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener URL pública
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error("Error al subir la imagen de perfil:", error);
    throw new Error("Error al subir la imagen de perfil a Firebase Storage");
  }
};

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      for (const profile of profiles) {
        const profileRef = doc(db, "users", userId, "promoProfiles", profile.id);
        const updatedData = {
          name: profile.name,
          description: profile.description,
          tags: Array.isArray(profile.tags)
            ? profile.tags.map((t) => t.trim())
            : typeof profile.tags === "string"
                ? profile.tags.split(",").map((t) => t.trim())
                : [],
          externalLinks: profile.externalLinks || null,
        };

        if (profile.newImage) {
          const imageUrl = await uploadProfileImage(profile.newImage);
          updatedData.profilePhotoUrl = imageUrl;
        }

        await updateDoc(profileRef, updatedData);
      }

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError("Error al guardar los cambios.");
    }

    setLoading(false);
  };

  const handleDelete = async () => {
    const profile = profiles[currentIndex];
    try {
      const ref = doc(db, "users", userId, "promoProfiles", profile.id);
      await updateDoc(ref, { status: "deleted" });

      const updated = [...profiles];
      updated.splice(currentIndex, 1);
      setProfiles(updated);
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } catch (err) {
      console.error(err);
      setError("Error al eliminar el perfil.");
    }
  };

  const handleToggleStatus = async () => {
    const profile = profiles[currentIndex];
    const newStatus = profile.status === "suspended" ? "active" : "suspended";

    try {
      const ref = doc(db, "users", userId, "promoProfiles", profile.id);
      await updateDoc(ref, { status: newStatus });

      const updated = [...profiles];
      updated[currentIndex].status = newStatus;
      setProfiles(updated);
    } catch (err) {
      console.error(err);
      setError("Error al cambiar el estado del perfil.");
    }
  };

  const currentProfile = profiles[currentIndex] || {};

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 dark:bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="scale-95 opacity-0"
              enterTo="scale-100 opacity-100"
              leave="ease-in duration-200"
              leaveFrom="scale-100 opacity-100"
              leaveTo="scale-95 opacity-0"
            >
              <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-white dark:bg-zinc-900 p-6 shadow-2xl transition-all space-y-6">
                <div className="flex justify-between items-center">
                  <Dialog.Title className="text-2xl font-bold text-gray-800 dark:text-white">
                    Editar Perfiles Promocionales
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-red-500 transition"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {error && (
                  <div className="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 px-4 py-2 rounded-md">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 px-4 py-2 rounded-md">
                    Cambios guardados con éxito.
                  </div>
                )}

                {profiles.length > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-gray-500 dark:text-gray-300">
                      <span>Perfil {currentIndex + 1} de {profiles.length}</span>
                      <div className="space-x-2">
                        <button
                          disabled={currentIndex === 0}
                          onClick={() => setCurrentIndex((prev) => prev - 1)}
                          className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition disabled:opacity-30"
                        >
                          <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                        <button
                          disabled={currentIndex === profiles.length - 1}
                          onClick={() => setCurrentIndex((prev) => prev + 1)}
                          className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition disabled:opacity-30"
                        >
                          <ChevronRightIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-4">
                        <Field
                          icon={<UserIcon className="w-4 h-4 mr-2" />}
                          label="Nombre"
                          name="name"
                          value={currentProfile.name || ""}
                          onChange={handleInputChange}
                        />
                        <Field
                          icon={<PencilIcon className="w-4 h-4 mr-2" />}
                          label="Descripción"
                          name="description"
                          value={currentProfile.description || ""}
                          onChange={handleInputChange}
                          textarea
                        />
                        <Field
                          icon={<TagIcon className="w-4 h-4 mr-2" />}
                          label="Etiquetas"
                          name="tags"
                          value={
                            Array.isArray(currentProfile.tags)
                              ? currentProfile.tags.join(", ")
                              : typeof currentProfile.tags === "string"
                                ? currentProfile.tags
                                : ""
                          }
                          onChange={handleInputChange}
                        />
                        <Field
                          icon={<LinkIcon className="w-4 h-4 mr-2" />}
                          label="Enlaces externos"
                          name="externalLinks"
                          value={currentProfile.externalLinks || ""}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            <PhotoIcon className="w-4 h-4 mr-2" />
                            Imagen de perfil
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="text-sm text-gray-600 dark:text-gray-300"
                          />
                          {currentProfile.profilePhotoUrl && (
                            <div className="mt-3">
                              <img
                                src={
                                  currentProfile.newImage
                                    ? URL.createObjectURL(currentProfile.newImage)
                                    : currentProfile.profilePhotoUrl
                                }
                                alt="preview"
                                className="w-32 h-32 object-cover rounded-xl shadow"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex flex-col-reverse sm:flex-row justify-between items-center gap-4 pt-4">
                  <div className="flex gap-2 w-full sm:w-auto justify-center">
                    <ActionButton
                      onClick={handleDelete}
                      color="red"
                      icon={<TrashIcon className="w-4 h-4 mr-1" />}
                      label="Eliminar"
                    />
                    <ActionButton
                      onClick={handleToggleStatus}
                      color="yellow"
                      icon={
                        currentProfile.status === "suspended"
                          ? <PlayIcon className="w-4 h-4 mr-1" />
                          : <PauseIcon className="w-4 h-4 mr-1" />
                      }
                      label={
                        currentProfile.status === "suspended"
                          ? "Activar"
                          : "Suspender"
                      }
                    />
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto justify-center">
                    <ActionButton
                      onClick={onClose}
                      color="gray"
                      label="Cancelar"
                    />
                    <ActionButton
                      onClick={handleSave}
                      color="blue"
                      label={loading ? "Guardando..." : "Guardar Cambios"}
                      disabled={loading}
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function Field({ icon, label, name, value, onChange, textarea = false }) {
  return (
    <div>
      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {icon}
        {label}
      </label>
      {textarea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          rows={3}
          className="w-full border rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-white"
        />
      ) : (
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          className="w-full border rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-700 dark:text-white"
        />
      )}
    </div>
  );
}

function ActionButton({ onClick, icon, label, color = "gray", disabled }) {
  const base = {
    red: "bg-red-600 hover:bg-red-700 text-white",
    yellow: "bg-yellow-500 hover:bg-yellow-600 text-white",
    gray: "bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-zinc-700 dark:text-white dark:hover:bg-zinc-600",
    blue: "bg-blue-600 hover:bg-blue-700 text-white",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium transition disabled:opacity-50 ${base[color]}`}
    >
      {icon}
      {label}
    </button>
  );
}
